import { LOGO_MARK_SVG } from "./logoMark";

/**
 * The mark ships with the design's three flat teals baked into its fills and
 * strokes. On the ink dark ground the two deep ones all but disappear, so they
 * are swapped for custom properties here and re-pointed at mint tints under
 * .dark (config/theme.tsx). The light values are the originals, so light mode
 * renders byte-identical to the design asset.
 */
const THEMED_MARK = LOGO_MARK_SVG
  .replaceAll("#1d4547", "var(--hl-logo-deep)")
  .replaceAll("#235456", "var(--hl-logo-mid)")
  .replaceAll("#a2dbd2", "var(--hl-logo-light)");

interface LogoProps {
  variant?: "light" | "dark";
  /** Rendered height, e.g. "32px". Width scales with it. */
  height?: string;
  className?: string;
}

/**
 * Brand lockup: the goat mark (vector, from the design file) + the HRMLESS
 * wordmark as LIVE TEXT in New Astro, "HRM" SemiBold and "LESS" Regular.
 *
 * Ported verbatim from `app/src/components/Logo.tsx`, with
 * two changes for this app:
 *   • sizing is an inline style rather than a Tailwind height class — Zudoku's
 *     Tailwind build does not scan this repo's design tokens.
 *   • the New Astro @font-face lives in config/theme.tsx customCss and points at
 *     /pub/newastro*.woff2 (this app's basePath), not /fonts.
 *
 * Geometry follows the design lockup: baseline y=38.2, cap height 29 units;
 * New Astro's cap height is 0.677em, hence font-size 44.
 */
export const Logo = ({ variant = "dark", height = "32px", className = "" }: LogoProps) => (
  // overflow=visible + the design's full 273-unit width: browsers shape the live
  // text a little wider than the calibration render, and an inline SVG clips past
  // its viewBox by default (the footer showed "LE" + a cut "SS" at 258).
  <svg
    viewBox="0 0 273 46.5"
    overflow="visible"
    role="img"
    aria-label="HRMLESS"
    className={className}
    style={{ display: "block", height, width: "auto", userSelect: "none" }}
  >
    <g dangerouslySetInnerHTML={{ __html: THEMED_MARK }} />
    <text
      x="84"
      y="38.2"
      fontFamily="'New Astro', 'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif"
      fontSize="44"
      fill={variant === "light" ? "#ffffff" : "var(--hl-logo-word)"}
    >
      <tspan fontWeight={600}>HRM</tspan>
      <tspan fontWeight={400}>LESS</tspan>
    </text>
  </svg>
);

export default Logo;
